import React from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'

// Components
import {
  OverlayPanel,
  OverlayPanelHeader,
  Panel,
  Text,
  ColoredCountUp,
  If,
  Unless,
} from '../../../../components'
import SavingsGraph from '../SavingsGraph'

// Hooks
import { useListingDisplay } from '../../../../hooks/listing'

// Helpers
import { formatAbbrCurrency } from '../../../../helpers'

// Styles
import styles from './ComparedToRenting.module.scss'

const ComparedToRenting = ({ handleBack }) => {
  const {
    savingsValue,
    breakEvenYears,
    yearsInHome,
    totalBuyCost,
    totalCurrentCost,
  } = useListingDisplay()
  const savingsCopy = savingsValue > 0 ? 'Saved' : 'Cost'

  return (
    <OverlayPanel>
      <OverlayPanelHeader onBack={handleBack}>
        <Text element='span' color='white' weight='semibold' size='l'>
          Compared to Renting
        </Text>
      </OverlayPanelHeader>
      <Panel className={styles.panel}>
        <Text color='eggShell' size='xxl' align='center'>
          <ColoredCountUp
            weight='light'
            value={savingsValue}
            formatter={formatAbbrCurrency}
          />
        </Text>
        <Text align='center'>{`${savingsCopy} compared to renting`}</Text>
      </Panel>
      <Panel className={classnames(styles.panel, styles.chart)}>
        <SavingsGraph />
      </Panel>
      <Text transform='uppercase' letterSpacing='large' weight='semibold'>
        Summary
      </Text>
      <hr />
      <Text color='white' className={styles.margin}>
        Compared to renting
        {`, buying this house could ${
          savingsValue >= 0 ? 'save' : 'cost'
        } you about`}
        <ColoredCountUp
          value={savingsValue}
          prefix=' '
          suffix=' '
          formatter={formatAbbrCurrency}
        />
        if you stayed for
        <Text
          element='span'
          weight='bold'
          color='white'
          className={styles.noWrap}
        >{` ${yearsInHome} years`}</Text>
      </Text>

      <Text color='white' className={styles.margin}>
        <If condition={breakEvenYears}>
          If you stay less than
          <Text
            element='span'
            weight='bold'
            color='white'
            className={styles.noWrap}
          >{` ${breakEvenYears} year${breakEvenYears !== 1 ? 's' : ''}`}</Text>
          , it could be better to rent
        </If>
        <Unless condition={breakEvenYears}>
          It is likely better to stay where you are
        </Unless>
      </Text>

      <Text color='white' className={styles.bottomText}>
        So your total cost of owning this home for
        <Text
          element='span'
          weight='bold'
          color='white'
          className={styles.noWrap}
        >{` ${yearsInHome} years`}</Text>
        {` would be about`}
        <Text
          element='span'
          weight='bold'
          color='white'
          className={styles.noWrap}
        >
          {` ${formatAbbrCurrency(Math.abs(totalBuyCost))}`}
        </Text>
        , while renting a similar place would be about
        <Text
          element='span'
          weight='bold'
          color='white'
          className={styles.noWrap}
        >
          {` ${formatAbbrCurrency(Math.abs(totalCurrentCost))}`}
        </Text>
      </Text>
    </OverlayPanel>
  )
}

ComparedToRenting.propTypes = {
  handleBack: PropTypes.func,
}

export default ComparedToRenting
